<template>
    <div class="moral-ops-panel fade-in">
        <MaintCollapsibleBanner
            title="历史德育分转积分"
            icon="el-icon-cpu"
            storage-key="moralHistoryPointConvert"
        >
            <div class="desc-item">
                <span class="desc-badge badge-primary">选择学校</span>
                <span>请选择左侧机构树中的 <strong>学校根节点</strong>（与日结 Job 的 schoolDepartmentId 一致）。</span>
            </div>
            <div class="desc-item">
                <span class="desc-badge badge-warning">无流水场景</span>
                <span><strong>仅有点评、没有流水</strong>：先做 <strong>步骤 1 补流水</strong>，再做 <strong>步骤 2</strong>。</span>
            </div>
            <div class="desc-item">
                <span class="desc-badge badge-success">正常场景</span>
                <span><strong>点评时已实时入账流水</strong>：步骤 1 可跳过（预览显示「已有流水」），直接 <strong>步骤 2 按日聚合 + 转积分</strong>；也可只点「仅日表聚合」或「仅转积分」。</span>
            </div>
        </MaintCollapsibleBanner>

        <div class="school-row">
            <el-card v-if="schoolDepartmentId > 0" shadow="never" class="scope-card accent-card">
                <div slot="header" class="card-header-styled">
                    <i class="el-icon-office-building"></i>
                    <span>学校范围</span>
                </div>
                <el-descriptions :column="3" size="medium" border class="custom-descriptions">
                    <el-descriptions-item label="学校" label-class-name="desc-label" content-class-name="desc-content">
                        <span class="highlight-text">{{ scopeInfo.SchoolName || '-' }}</span>
                    </el-descriptions-item>
                    <el-descriptions-item label="SchId" label-class-name="desc-label" content-class-name="desc-content">
                        <code>{{ scopeInfo.SchId || '-' }}</code>
                    </el-descriptions-item>
                    <el-descriptions-item label="学校部门Id" label-class-name="desc-label" content-class-name="desc-content">
                        <code>{{ scopeInfo.SchoolDepartmentId || schoolDepartmentId || '-' }}</code>
                    </el-descriptions-item>
                    <el-descriptions-item label="班级数" label-class-name="desc-label" content-class-name="desc-content">
                        <span class="badge-count">{{ scopeInfo.ClassDepartmentCount }}</span>
                    </el-descriptions-item>
                    <el-descriptions-item label="日结转积分" label-class-name="desc-label" content-class-name="desc-content">
                        <div class="status-indicator">
                            <span :class="['status-dot', scopeInfo.PointDailyConversionEnabled ? 'dot-active' : 'dot-inactive']"></span>
                            <span :class="['status-text', scopeInfo.PointDailyConversionEnabled ? 'text-active' : 'text-inactive']">
                                {{ scopeInfo.PointDailyConversionEnabled ? '已开启日结' : '未开启日结' }}
                            </span>
                        </div>
                    </el-descriptions-item>
                    <el-descriptions-item label="模式" label-class-name="desc-label" content-class-name="desc-content">
                        <el-tag size="small" :type="scopeInfo.IsZheshangWest ? 'warning' : 'info'" effect="dark" class="mode-tag">
                            {{ scopeInfo.IsZheshangWest ? '哲商西校区模式' : '标准通用模式' }}
                        </el-tag>
                    </el-descriptions-item>
                </el-descriptions>
            </el-card>
            <div v-else class="empty-state-alert">
                <i class="el-icon-info empty-icon"></i>
                <div class="empty-text">请在顶栏下拉切换中选择一所学校以开始配置运维工作</div>
            </div>
        </div>

        <!-- 配置参数区 -->
        <el-card shadow="never" class="step-card">
            <div slot="header" class="card-header-styled">
                <i class="el-icon-date"></i>
                <span>配置执行日期范围</span>
            </div>
            <div class="date-picker-wrapper">
                <el-date-picker
                    v-model="dateRange"
                    type="daterange"
                    start-placeholder="开始日期"
                    end-placeholder="结束日期"
                    value-format="yyyy-MM-dd"
                    :disabled="schoolDepartmentId <= 0"
                    class="custom-date-picker"
                />
            </div>
        </el-card>

        <!-- 步骤 1 卡片 -->
        <el-card shadow="never" class="step-card branch-card">
            <div slot="header" class="card-header-styled">
                <span class="step-badge">步骤 1</span>
                <span>补德育分流水</span>
            </div>
            
            <div class="actions-wrapper">
                <el-button size="medium" :loading="previewLoading" :disabled="!canRun" @click="doPreview" class="btn-modern btn-secondary">
                    <i class="el-icon-view"></i> 预览待补条数
                </el-button>
                <el-button size="medium" type="warning" :loading="backfillLoading" :disabled="!canRun" @click="doBackfillDryRun" class="btn-modern btn-warning">
                    <i class="el-icon-document-checked"></i> 试运行补流水
                </el-button>
                <el-button size="medium" type="primary" :loading="backfillLoading" :disabled="!canRun" @click="doBackfill" class="btn-modern btn-primary">
                    <i class="el-icon-video-play"></i> 执行补流水
                </el-button>
            </div>

            <!-- 数据结果看板化展示 -->
            <div v-if="previewResult" class="result-box fade-in-up">
                <div class="stat-dashboard">
                    <div class="stat-card stat-blue">
                        <div class="stat-info">
                            <span class="stat-label">待补流水总数</span>
                            <span class="stat-value">{{ previewResult.NeedLedgerCount }}<span class="unit">条</span></span>
                        </div>
                        <div class="stat-icon-bg"><i class="el-icon-circle-plus-outline"></i></div>
                    </div>
                    <div class="stat-card stat-gray">
                        <div class="stat-info">
                            <span class="stat-label">已有流水总数</span>
                            <span class="stat-value">{{ previewResult.AlreadyHasLedgerCount }}<span class="unit">条</span></span>
                        </div>
                        <div class="stat-icon-bg"><i class="el-icon-folder-checked"></i></div>
                    </div>
                </div>

                <div v-if="previewResult.ByBizDate && previewResult.ByBizDate.length" class="table-section">
                    <div class="table-title"><i class="el-icon-tickets"></i> 业务日待补明细清单</div>
                    <el-table :data="previewResult.ByBizDate" size="medium" max-height="240" class="custom-table" border>
                        <el-table-column prop="BizDate" label="业务日期" width="160" align="center" />
                        <el-table-column prop="NeedCount" label="待补条数" align="right">
                            <template slot-scope="scope">
                                <span class="table-count-highlight">{{ scope.row.NeedCount }} 条</span>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
            </div>
        </el-card>

        <!-- 步骤 2 卡片 -->
        <el-card shadow="never" class="step-card branch-card">
            <div slot="header" class="card-header-styled">
                <span class="step-badge">步骤 2</span>
                <span>按日结转与积分转换</span>
            </div>
            
            <div class="actions-wrapper">
                <el-button size="medium" :loading="ledgerPreviewLoading" :disabled="!canRun" @click="doLedgerPreview" class="btn-modern btn-secondary">
                    <i class="el-icon-zoom-in"></i> 预览待聚合与结转
                </el-button>
                <el-button size="medium" type="primary" :loading="pipelineLoading" :disabled="!canRun" @click="doPipeline" class="btn-modern btn-primary-alt">
                    <i class="el-icon-refresh"></i> 执行逐日聚合 + 转积分
                </el-button>
            </div>

            <!-- 数据结果看板化展示 -->
            <div v-if="ledgerPreviewResult" class="result-box fade-in-up">
                <div class="stat-dashboard">
                    <div class="stat-card stat-orange">
                        <div class="stat-info">
                            <span class="stat-label">待日表聚合流水</span>
                            <span class="stat-value">{{ ledgerPreviewResult.PendingRollupLedgerCount }}<span class="unit">条</span></span>
                        </div>
                        <div class="stat-icon-bg"><i class="el-icon-pie-chart"></i></div>
                    </div>
                    <div class="stat-card stat-indigo">
                        <div class="stat-info">
                            <span class="stat-label">待结转积分流水</span>
                            <span class="stat-value">{{ ledgerPreviewResult.PendingConvertLedgerCount }}<span class="unit">条</span></span>
                        </div>
                        <div class="stat-icon-bg"><i class="el-icon-refresh-left"></i></div>
                    </div>
                </div>

                <div v-if="ledgerPreviewResult.ByBizDate && ledgerPreviewResult.ByBizDate.length" class="table-section">
                    <div class="table-title"><i class="el-icon-tickets"></i> 业务日待结转明细</div>
                    <el-table :data="ledgerPreviewResult.ByBizDate" size="medium" max-height="240" class="custom-table" border>
                        <el-table-column prop="BizDate" label="业务日期" width="160" align="center" />
                        <el-table-column prop="PendingRollup" label="待聚合条数" align="right">
                            <template slot-scope="scope">
                                <span class="table-count-pending">{{ scope.row.PendingRollup }} 条</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="PendingConvert" label="待结转条数" align="right">
                            <template slot-scope="scope">
                                <span class="table-count-convert">{{ scope.row.PendingConvert }} 条</span>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
            </div>

            <!-- 单日指定控制台 -->
            <div class="single-date-panel">
                <div class="panel-inner-title">
                    <i class="el-icon-setting"></i>
                    <span>单日精细运维控制台</span>
                </div>
                <div class="single-ops-row">
                    <div class="input-with-label">
                        <span class="input-label">指定业务日：</span>
                        <el-date-picker
                            v-model="singleBizDate"
                            type="date"
                            placeholder="业务日"
                            value-format="yyyy-MM-dd"
                            style="width: 180px;"
                            size="small"
                            class="compact-input-picker"
                        />
                    </div>
                    <div class="button-group">
                        <el-button size="small" :loading="rollupLoading" :disabled="!canSingleRun" @click="doRollup" class="btn-modern-small btn-outline-warning">
                            <i class="el-icon-pie-chart"></i> 仅日表聚合
                        </el-button>
                        <el-button size="small" :loading="convertLoading" :disabled="!canSingleRun" @click="doConvert" class="btn-modern-small btn-outline-primary">
                            <i class="el-icon-refresh"></i> 仅转积分
                        </el-button>
                    </div>
                </div>
            </div>

            <!-- 管道流水线执行结果 -->
            <div v-if="pipelineResult" class="result-box fade-in-up">
                <div class="stat-dashboard">
                    <div class="stat-card stat-teal">
                        <div class="stat-info">
                            <span class="stat-label">管道日表聚合成功</span>
                            <span class="stat-value">{{ pipelineResult.TotalRollupGroups }}<span class="unit">组</span></span>
                        </div>
                        <div class="stat-icon-bg"><i class="el-icon-circle-check"></i></div>
                    </div>
                    <div class="stat-card stat-emerald">
                        <div class="stat-info">
                            <span class="stat-label">管道积分结转成功</span>
                            <span class="stat-value">{{ pipelineResult.TotalConvertGroups }}<span class="unit">组</span></span>
                        </div>
                        <div class="stat-icon-bg"><i class="el-icon-success"></i></div>
                    </div>
                </div>

                <div class="table-section">
                    <div class="table-title"><i class="el-icon-notebook-2"></i> 逐日流水线管道运行报告</div>
                    <el-table :data="pipelineResult.Days" size="medium" max-height="320" class="custom-table" border>
                        <el-table-column prop="BizDate" label="日期" width="130" align="center" />
                        <el-table-column prop="RollupCount" label="聚合组数" width="100" align="right" />
                        <el-table-column prop="RollupMessage" label="聚合说明" show-overflow-tooltip />
                        <el-table-column prop="ConvertCount" label="结转组数" width="100" align="right" />
                        <el-table-column prop="ConvertMessage" label="结转说明" show-overflow-tooltip />
                    </el-table>
                </div>
            </div>
        </el-card>
    </div>
</template>

<script>
import { moralOpsGetSchoolScope, moralOpsPreviewBackfillLedger, moralOpsPreviewLedgerPipeline, moralOpsBackfillLedgerFromEvaluation, moralOpsRunDailyRollup, moralOpsRunConvertDailyToPoints, moralOpsRunDailyPipelineRange } from '../../../api/modules/moral-education';
import MaintCollapsibleBanner from '../components/MaintCollapsibleBanner';

export default {
    name: 'MoralHistoryPointConvert',
    components: { MaintCollapsibleBanner },
    data() {
        return {
            schoolDepartmentId: 0,
            scopeInfo: {},
            dateRange: [],
            singleBizDate: '',
            previewLoading: false,
            ledgerPreviewLoading: false,
            ledgerPreviewResult: null,
            backfillLoading: false,
            rollupLoading: false,
            convertLoading: false,
            pipelineLoading: false,
            previewResult: null,
            pipelineResult: null
        };
    },
    computed: {
        canRun() {
            return this.schoolDepartmentId > 0 && this.dateRange && this.dateRange.length === 2;
        },
        canSingleRun() {
            // 只要选择了学校，且（选择了日期范围 或 指定了单日业务日）即可点击单日操作
            const hasSchool = this.schoolDepartmentId > 0;
            const hasDateRange = this.dateRange && this.dateRange.length === 2;
            const hasSingleDate = !!this.singleBizDate;
            return hasSchool && (hasDateRange || hasSingleDate);
        },
        requestDates() {
            if (!this.dateRange || this.dateRange.length !== 2) return { dateFrom: null, dateTo: null };
            return { dateFrom: this.dateRange[0], dateTo: this.dateRange[1] };
        }
    },
    watch: {
        // 监听全局选中的学校 ID 变化，自动联动重载该学校范围配置信息
        '$store.state.activeSchoolId': {
            handler(newVal) {
                if (newVal) {
                    this.schoolDepartmentId = Number(newVal);
                    this.loadScope();
                } else {
                    this.schoolDepartmentId = 0;
                    this.scopeInfo = {};
                }
            },
            immediate: true
        },
        // 新增：监听执行日期范围，强制限制最多只能配置一个月以保障线上运行安全
        dateRange(val) {
            if (val && val.length === 2) {
                const d1 = new Date(val[0]);
                const d2 = new Date(val[1]);
                const diffTime = Math.abs(d2 - d1);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                if (diffDays > 31) {
                    this.$message.warning("为保障线上数据运行安全与系统性能，日期跨度最多只能选择一个月（31天），请重新选择范围");
                    this.dateRange = [];
                }
            }
        }
    },
    methods: {
        loadScope() {
            moralOpsGetSchoolScope({ schoolDepartmentId: this.schoolDepartmentId }).then(res => {
                if (res.data && res.data.success) {
                    this.scopeInfo = res.data.response || {};
                } else {
                    this.$message.error((res.data && res.data.msg) || '加载学校范围失败');
                }
            });
        },
        buildBody(extra) {
            return {
                SchoolDepartmentId: this.schoolDepartmentId,
                DateFrom: this.requestDates.dateFrom,
                DateTo: this.requestDates.dateTo,
                ...extra
            };
        },
        doLedgerPreview() {
            this.ledgerPreviewLoading = true;
            moralOpsPreviewLedgerPipeline(this.buildBody()).then(res => {
                this.ledgerPreviewLoading = false;
                if (res.data && res.data.success) {
                    this.ledgerPreviewResult = res.data.response;
                } else {
                    this.$message.error((res.data && res.data.msg) || '预览失败');
                }
            }).catch(() => { this.ledgerPreviewLoading = false; });
        },
        doPreview() {
            this.previewLoading = true;
            moralOpsPreviewBackfillLedger(this.buildBody()).then(res => {
                this.previewLoading = false;
                if (res.data && res.data.success) {
                    this.previewResult = res.data.response;
                } else {
                    this.$message.error((res.data && res.data.msg) || '预览失败');
                }
            }).catch(() => { this.previewLoading = false; });
        },
        doBackfillDryRun() {
            this.confirmRun('试运行补流水（不写库）', () => this.runBackfill(true));
        },
        doBackfill() {
            this.confirmRun('确认执行补流水？', () => this.runBackfill(false));
        },
        runBackfill(dryRun) {
            this.backfillLoading = true;
            moralOpsBackfillLedgerFromEvaluation(this.buildBody({ dryRun })).then(res => {
                this.backfillLoading = false;
                if (res.data && res.data.success) {
                    const r = res.data.response || {};
                    this.$message.success(r.Message || res.data.msg);
                    this.doPreview();
                } else {
                    this.$message.error((res.data && res.data.msg) || '补流水失败');
                }
            }).catch(() => { this.backfillLoading = false; });
        },
        doRollup() {
            const biz = this.singleBizDate || this.requestDates.dateTo;
            if (!biz) return this.$message.warning('请填写单日或选择日期范围');
            this.rollupLoading = true;
            moralOpsRunDailyRollup({ bizDate: biz, schoolDepartmentId: this.schoolDepartmentId }).then(res => {
                this.rollupLoading = false;
                this.handleStepResult(res);
            }).catch(() => { this.rollupLoading = false; });
        },
        doConvert() {
            const biz = this.singleBizDate || this.requestDates.dateTo;
            if (!biz) return this.$message.warning('请填写单日或选择日期范围');
            this.convertLoading = true;
            moralOpsRunConvertDailyToPoints({ bizDate: biz, schoolDepartmentId: this.schoolDepartmentId }).then(res => {
                this.convertLoading = false;
                this.handleStepResult(res);
            }).catch(() => { this.convertLoading = false; });
        },
        doPipeline() {
            this.confirmRun('确认按日执行 Rollup + 转积分？耗时与日期跨度有关。', () => {
                this.pipelineLoading = true;
                moralOpsRunDailyPipelineRange(this.buildBody()).then(res => {
                    this.pipelineLoading = false;
                    if (res.data && res.data.success) {
                        this.pipelineResult = res.data.response;
                        this.$message.success(res.data.msg);
                    } else {
                        this.$message.error((res.data && res.data.msg) || '管道执行失败');
                    }
                }).catch(() => { this.pipelineLoading = false; });
            });
        },
        handleStepResult(res) {
            if (res.data && res.data.success) {
                const r = res.data.response || {};
                this.$message.success(r.Message || res.data.msg);
            } else {
                this.$message.error((res.data && res.data.msg) || '执行失败');
            }
        },
        confirmRun(title, fn) {
            this.$confirm(title, '提示', { type: 'warning' }).then(() => fn()).catch(() => {});
        }
    }
};
</script>

<style scoped>
/* 根容器：在 Hub flex 布局内占满高度并允许纵向滚动 */
.moral-ops-panel {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 4px;
    padding-bottom: 12px;
    scrollbar-width: thin;
    scrollbar-color: #94a3b8 #f1f5f9;
}

.moral-ops-panel::-webkit-scrollbar {
    width: 6px;
}

.moral-ops-panel::-webkit-scrollbar-thumb {
    background: #94a3b8;
    border-radius: 4px;
}

.moral-ops-panel::-webkit-scrollbar-thumb:hover {
    background: #64748b;
}

.moral-ops-panel::-webkit-scrollbar-track {
    background: #f1f5f9;
}

/* 全局页面淡入与精致排版 */
.fade-in {
    animation: fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
}

.fade-in-up {
    animation: fadeInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

@keyframes fadeInUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
}

/* 精致布局与学校范围 */
.school-row {
    margin-bottom: 20px;
}

.school-row ::v-deep > .el-col {
    display: flex;
    flex-direction: column;
}

.dept-pane-wrapper {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 12px;
    min-height: 240px;
    max-height: 320px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
    overflow-y: auto;
    overflow-x: hidden;
}

.card-header-styled {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    font-size: 14px;
    color: #1e293b;
}

.card-header-styled i {
    color: #6366f1;
    font-size: 16px;
}

.accent-card {
    border-top: 4px solid #4f46e5 !important;
}

.scope-card {
    border-radius: 12px !important;
    border: 1px solid #e2e8f0;
    flex: 1;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
}

/* 描述列表表格微美化 */
::v-deep .custom-descriptions .el-descriptions__table {
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e2e8f0;
}

::v-deep .custom-descriptions .desc-label {
    background: #f8fafc !important;
    color: #475569;
    font-weight: 600;
    width: 120px;
}

::v-deep .custom-descriptions .desc-content {
    color: #1e293b;
}

.highlight-text {
    font-weight: 600;
    color: #4f46e5;
}

.badge-count {
    background: #eff6ff;
    color: #2563eb;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    border: 1px solid #dbeafe;
}

/* 日结转呼吸灯指示器 */
.status-indicator {
    display: flex;
    align-items: center;
    gap: 6px;
}

.status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
}

.dot-active {
    background: #10b981;
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
    animation: statusPulse 2s infinite;
}

.dot-inactive {
    background: #cbd5e1;
}

.status-text {
    font-size: 12px;
    font-weight: 500;
}

.text-active { color: #047857; }
.text-inactive { color: #64748b; }

@keyframes statusPulse {
    0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
    70% { transform: scale(1); box-shadow: 0 0 0 4px rgba(16, 185, 129, 0); }
    100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
}

.mode-tag {
    border-radius: 6px;
    font-weight: 600;
}

/* 空白提示 */
.empty-state-alert {
    background: #f8fafc;
    border: 2px dashed #cbd5e1;
    border-radius: 12px;
    padding: 32px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-height: 240px;
}

.empty-icon {
    font-size: 28px;
    color: #94a3b8;
}

.empty-text {
    color: #64748b;
    font-size: 13px;
    font-weight: 500;
}

/* 卡片和日期范围 */
.step-card {
    border-radius: 12px !important;
    border: 1px solid #e2e8f0;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02) !important;
    margin-top: 16px;
    transition: box-shadow 0.3s;
}

::v-deep .step-card .el-card__header {
    padding: 12px 18px;
}

::v-deep .step-card .el-card__body {
    padding: 14px 18px;
}

.step-card:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.04), 0 4px 6px -2px rgba(0, 0, 0, 0.02) !important;
}

.branch-card {
    border-left: 4px solid #6366f1 !important;
}

.date-picker-wrapper {
    padding: 8px 0;
}

::v-deep .custom-date-picker.el-range-editor.el-input__inner {
    border-radius: 8px;
    border: 1px solid #cbd5e1;
    transition: all 0.3s;
    height: 40px;
    line-height: 40px;
}

::v-deep .custom-date-picker.is-active,
::v-deep .custom-date-picker.el-range-editor.el-input__inner:hover {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

/* 步骤指示微标 */
.step-badge {
    background: #6366f1;
    color: #ffffff;
    padding: 2px 8px;
    font-size: 11px;
    font-weight: 700;
    border-radius: 4px;
    margin-right: 8px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
}

/* 按钮操作排版与微动效 */
.actions-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
}

.btn-modern {
    border-radius: 8px !important;
    font-weight: 600 !important;
    padding: 10px 18px !important;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.btn-modern i {
    font-size: 14px;
}

.btn-modern:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.btn-modern:active {
    transform: translateY(0);
}

/* 辅助灰色按钮 */
.btn-secondary {
    background: #f1f5f9 !important;
    color: #475569 !important;
    border: 1px solid #cbd5e1 !important;
}

.btn-secondary:hover {
    background: #e2e8f0 !important;
    color: #1e293b !important;
}

/* 警告黄色按钮 */
.btn-warning {
    background: linear-gradient(135deg, #d97706 0%, #b45309 100%) !important;
    border: none !important;
    color: #ffffff !important;
}

.btn-warning:hover {
    opacity: 0.95;
    box-shadow: 0 4px 12px rgba(217, 119, 6, 0.3) !important;
}

/* 主运行蓝色按钮 */
.btn-primary {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
    border: none !important;
    color: #ffffff !important;
}

.btn-primary:hover {
    opacity: 0.95;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3) !important;
}

/* 备用靛蓝按钮 */
.btn-primary-alt {
    background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%) !important;
    border: none !important;
    color: #ffffff !important;
}

.btn-primary-alt:hover {
    opacity: 0.95;
    box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3) !important;
}

/* 指标卡片看板 */
.stat-dashboard {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
}

.stat-card {
    position: relative;
    padding: 20px;
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.01), 0 2px 4px -1px rgba(0, 0, 0, 0.01);
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.stat-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    z-index: 2;
}

.stat-label {
    font-size: 12px;
    font-weight: 600;
}

.stat-value {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.1;
    font-family: 'Outfit', sans-serif;
}

.stat-value .unit {
    font-size: 13px;
    font-weight: 500;
    margin-left: 4px;
}

.stat-icon-bg {
    font-size: 40px;
    opacity: 0.12;
    z-index: 1;
    position: absolute;
    right: 16px;
    bottom: 8px;
}

/* 调色板 - 指标卡 */
.stat-blue {
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    color: #1e40af;
}
.stat-gray {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    color: #475569;
}
.stat-orange {
    background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
    color: #c2410c;
}
.stat-indigo {
    background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
    color: #3730a3;
}
.stat-teal {
    background: linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%);
    color: #0f766e;
}
.stat-emerald {
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
    color: #166534;
}

/* 表格区域美化 */
.table-section {
    margin-top: 16px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 16px;
}

.table-title {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
}

.table-title i {
    color: #6366f1;
}

/* 深度美化 Element 表格 */
::v-deep .custom-table.el-table {
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e2e8f0;
}

::v-deep .custom-table.el-table th {
    background: #f8fafc !important;
    color: #475569 !important;
    font-weight: 600 !important;
    height: 42px;
    padding: 0;
}

::v-deep .custom-table.el-table td {
    padding: 8px 0;
    color: #334155;
}

::v-deep .custom-table.el-table--enable-row-hover .el-table__body tr:hover > td {
    background-color: #f1f5f9 !important;
}

.table-count-highlight {
    font-weight: 600;
    color: #2563eb;
}

.table-count-pending {
    font-weight: 600;
    color: #ea580c;
}

.table-count-convert {
    font-weight: 600;
    color: #4f46e5;
}

/* 单日操作面板 */
.single-date-panel {
    margin-top: 24px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 16px 20px;
}

.panel-inner-title {
    font-size: 13px;
    font-weight: 600;
    color: #334155;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
}

.panel-inner-title i {
    color: #d97706;
}

.single-ops-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.input-with-label {
    display: flex;
    align-items: center;
    gap: 8px;
}

.input-label {
    font-size: 13px;
    font-weight: 500;
    color: #64748b;
}

::v-deep .compact-input-picker.el-input,
::v-deep .compact-input-picker .el-input__inner {
    border-radius: 6px;
    border: 1px solid #cbd5e1;
    transition: all 0.3s;
}

::v-deep .compact-input-picker.is-active .el-input__inner,
::v-deep .compact-input-picker .el-input__inner:focus {
    border-color: #f59e0b;
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.15);
}

.button-group {
    display: flex;
    gap: 8px;
}

.btn-modern-small {
    border-radius: 6px !important;
    font-weight: 600 !important;
    padding: 8px 14px !important;
    transition: all 0.2s !important;
}

.btn-modern-small:hover {
    transform: translateY(-1px);
}

.btn-outline-warning {
    background: #ffffff !important;
    color: #d97706 !important;
    border: 1px solid #f59e0b !important;
}

.btn-outline-warning:hover {
    background: #fffbeb !important;
    box-shadow: 0 2px 8px rgba(217, 119, 6, 0.1) !important;
}

.btn-outline-primary {
    background: #ffffff !important;
    color: #2563eb !important;
    border: 1px solid #3b82f6 !important;
}

.btn-outline-primary:hover {
    background: #eff6ff !important;
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.1) !important;
}
</style>

